import { getMfaState, removeTotp } from "@/features/auth/api/mfa";
import { usersOrganisationsRolesQueryOptions } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { isPrivilegedUser } from "@/features/auth/utils/mfaRouting";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useAuthCopy } from "@/features/i18n/authCopy";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { ui } from "@patient-watch/ui";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

function MfaSettingsPage() {
    const copy = useAuthCopy();
    const actionCopy = useActionCopy();
    const navigate = useNavigate();
    const [factorId, setFactorId] = useState("");
    const [error, setError] = useState("");
    const [pending, setPending] = useState(false);

    useEffect(() => {
        void getMfaState()
            .then((state) => setFactorId(state.verifiedTotp[0]?.id ?? ""))
            .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : copy.somethingWentWrong));
    }, [copy.somethingWentWrong]);

    const replace = async () => {
        if (!factorId) return;
        setPending(true);
        try {
            await removeTotp(factorId);
            void navigate({ to: "/mfa/enroll", search: { returnTo: "/profile/mfa" }, replace: true });
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : copy.somethingWentWrong);
            setPending(false);
        }
    };

    return (
        <div className="mx-auto w-full max-w-2xl space-y-8 py-4">
            <div className="flex items-start gap-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <ShieldCheck className="size-7" aria-hidden="true" />
                </div>
                <div className="space-y-1.5 pt-0.5">
                    <h1 className="font-bold text-2xl tracking-tight">{copy.mfaSettingsTitle}</h1>
                    <p className="max-w-xl text-muted-foreground leading-relaxed">{copy.mfaSettingsDescription}</p>
                </div>
            </div>
            {factorId ? (
                <ui.Card className="overflow-hidden border-primary/15 bg-card/90 shadow-sm">
                    <ui.CardContent className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                        <div className="flex items-center gap-3">
                            <span className="relative flex size-3" aria-hidden="true">
                                <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-40" />
                                <span className="relative inline-flex size-3 rounded-full bg-emerald-500" />
                            </span>
                            <div>
                                <p className="font-semibold">{copy.mfaFactorActive}</p>
                                <p className="text-sm text-muted-foreground">TOTP</p>
                            </div>
                        </div>
                        <ui.AlertDialog>
                            <ui.AlertDialogTrigger asChild>
                                <ui.Button className="w-full sm:w-auto" variant="outline" disabled={pending}>
                                    {copy.mfaReplaceFactor}
                                </ui.Button>
                            </ui.AlertDialogTrigger>
                            <ui.AlertDialogContent className="max-w-md rounded-2xl border-primary/15 p-0">
                                <div className="border-b border-border/70 p-6 pb-5">
                                    <div className="mb-4 flex size-11 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-700">
                                        <ShieldAlert className="size-6" aria-hidden="true" />
                                    </div>
                                    <ui.AlertDialogHeader>
                                        <ui.AlertDialogTitle>{copy.mfaReplaceFactor}</ui.AlertDialogTitle>
                                        <ui.AlertDialogDescription className="leading-relaxed">
                                            {copy.mfaReplaceConfirm}
                                        </ui.AlertDialogDescription>
                                    </ui.AlertDialogHeader>
                                </div>
                                <ui.AlertDialogFooter className="gap-2 bg-muted/40 p-4 sm:space-x-0">
                                    <ui.AlertDialogCancel disabled={pending}>{actionCopy.cancel}</ui.AlertDialogCancel>
                                    <ui.AlertDialogAction asChild>
                                        <ui.Button disabled={pending} onClick={() => void replace()}>
                                            {copy.mfaReplaceFactor}
                                        </ui.Button>
                                    </ui.AlertDialogAction>
                                </ui.AlertDialogFooter>
                            </ui.AlertDialogContent>
                        </ui.AlertDialog>
                    </ui.CardContent>
                </ui.Card>
            ) : !error ? (
                <p>{copy.mfaLoading}</p>
            ) : null}
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
        </div>
    );
}

export const Route = createFileRoute("/_app/profile/mfa")({
    component: MfaSettingsPage,
    loader: async ({ context }) => {
        const [profile, roles] = await Promise.all([
            context.queryClient.ensureQueryData(selectProfileByIdQueryOptions(context.auth.id)),
            context.queryClient.ensureQueryData(usersOrganisationsRolesQueryOptions(context.auth.id)),
        ]);
        if (
            !isPrivilegedUser({
                isAdmin: roles?.some((role) => role.role === "ADMIN") ?? false,
                isSuperUser: profile?.is_super_user ?? false,
            })
        ) {
            throw redirect({ to: "/profile" });
        }
    },
});
